import { Check, Copy, Plus, Trash2, X, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AnswerShape, ANSWER_COLORS } from "@/components/game/AnswerShape";
import { questionIssues, uid, move } from "@/lib/quizFactory";

const TIMES = [5, 10, 20, 30, 45, 60, 90, 120, 240];
export const selectCls = "h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

export const QuestionEditor = ({ quiz, sel, setSel, update, t }) => {
  const c = quiz.customers[sel.c];
  const q = c.questions[sel.q];
  const upd = (fn) => update((qz) => fn(qz.customers[sel.c].questions[sel.q]));
  const issues = questionIssues(q);

  const duplicate = () => {
    update((qz) => qz.customers[sel.c].questions.splice(sel.q + 1, 0, { ...structuredClone(q), id: uid() }));
    setSel({ ...sel, q: sel.q + 1 });
  };
  const remove = () => {
    update((qz) => qz.customers[sel.c].questions.splice(sel.q, 1));
    setSel({ c: sel.c, q: c.questions.length > 1 ? Math.max(0, sel.q - 1) : null });
  };
  const moveQ = (dir) => {
    const j = sel.q + dir;
    if (j < 0 || j >= c.questions.length) return;
    update((qz) => move(qz.customers[sel.c].questions, sel.q, dir));
    setSel({ ...sel, q: j });
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-7" data-testid="question-editor">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{t("customer")} {sel.c + 1} · {c.name}</div>
          <h2 className="text-2xl font-bold">{t("question")} {sel.q + 1}</h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" title={t("move_up")} data-testid="question-move-up" onClick={() => moveQ(-1)}><ArrowUp className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" title={t("move_down")} data-testid="question-move-down" onClick={() => moveQ(1)}><ArrowDown className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" title={t("duplicate_question")} data-testid="question-duplicate-button" onClick={duplicate}><Copy className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" title={t("delete_question")} data-testid="question-delete-button" onClick={remove}><Trash2 className="w-4 h-4 text-rose-400" /></Button>
        </div>
      </div>

      <div className="grid gap-2">
        <div className="flex justify-between"><Label>{t("question_text")}</Label><span className="text-xs text-muted-foreground">{q.text.length}/200</span></div>
        <Textarea data-testid="quiz-builder-question-text-input" rows={3} maxLength={200} className="text-lg resize-none"
          placeholder={t("question_placeholder")} value={q.text} onChange={(e) => upd((x) => { x.text = e.target.value; })} />
      </div>

      <div className="grid gap-3">
        <Label>{t("answer_options")} ({q.options.length}/6)</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          {q.options.map((o, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-lg p-2 pr-2.5 border-2 transition-colors ${o.correct ? "border-emerald-400" : "border-transparent"}`} style={{ background: `${ANSWER_COLORS[i]}26` }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: ANSWER_COLORS[i] }}><AnswerShape i={i} size="20px" /></div>
              <input data-testid={`quiz-builder-option-input-${i + 1}`} maxLength={90} value={o.text} placeholder={t("option_placeholder", { n: i + 1 })}
                onChange={(e) => upd((x) => { x.options[i].text = e.target.value; })}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm font-semibold placeholder:text-muted-foreground/70" />
              <button data-testid={`quiz-builder-correct-checkbox-${i + 1}`} title={t("correct_toggle")} onClick={() => upd((x) => { x.options[i].correct = !x.options[i].correct; })}
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${o.correct ? "bg-emerald-500 border-emerald-500 text-white" : "border-muted-foreground/40 text-transparent hover:border-emerald-400"}`}>
                <Check className="w-4 h-4" strokeWidth={3} />
              </button>
              {q.options.length > 2 && (
                <button data-testid={`quiz-builder-remove-option-${i + 1}`} title={t("remove")} onClick={() => upd((x) => { x.options.splice(i, 1); })} className="text-muted-foreground hover:text-rose-400"><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
        {q.options.length < 6 && (
          <Button variant="secondary" className="w-fit" data-testid="quiz-builder-add-option-button" onClick={() => upd((x) => { x.options.push({ text: "", correct: false }); })}>
            <Plus className="w-4 h-4 mr-1.5" />{t("add_option")}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-6">
        <div className="grid gap-2">
          <Label>{t("time_limit")}</Label>
          <select data-testid="quiz-builder-time-limit-select" className={selectCls} value={q.time_limit} onChange={(e) => upd((x) => { x.time_limit = Number(e.target.value); })}>
            {TIMES.map((s) => <option key={s} value={s}>{s} {t("seconds")}</option>)}
          </select>
        </div>
        <div className="grid gap-2">
          <Label>{t("points")}</Label>
          <select data-testid="quiz-builder-points-select" className={selectCls} value={q.points} onChange={(e) => upd((x) => { x.points = e.target.value; })}>
            {["standard", "double", "none"].map((p) => <option key={p} value={p}>{t(`points_${p}`)}</option>)}
          </select>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-300" data-testid="question-issues">
          {issues.map((k) => t(k)).join(" · ")}
        </div>
      )}
    </div>
  );
};
